/**
 * MulliganModal — opening-hand decision UI for ranked-mode matches.
 *
 * Per Wonders rules (and rule_engine._execute_mulligan):
 *   - Each player draws 7 cards opening hand.
 *   - Up to 3 cards may be buried to the BOTTOM of the deck.
 *   - The same number of cards is drawn off the TOP as replacements.
 *   - One-shot per player. Fired only when state.phase === 'mulligan'
 *     and state.mulliganUsed?.[viewer] === false.
 *
 * The modal is purely presentational: parent owns the hand state and
 * dispatches the Mulligan action via `onSubmit(cardIds)`. The empty
 * cardIds case is "keep all cards" — also dispatches so the rule engine
 * marks mulliganUsed.
 */

import { type ReactElement, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CardData } from '../types.js';

type Props = {
  readonly hand: readonly string[];
  readonly cards: ReadonlyMap<string, CardData>;
  readonly imageUrl: (cardId: string) => string;
  readonly onSubmit: (cardIds: readonly string[]) => void;
  readonly onCancel?: () => void;
};

const MAX_MULLIGAN = 3;

export function MulliganModal({ hand, cards, imageUrl, onSubmit, onCancel }: Props): ReactElement {
  // Track picks by hand-index, not card-id: duplicates of the same card
  // share a card-id but live in distinct hand slots, and the player must
  // be able to bury them independently.
  const [picked, setPicked] = useState<readonly number[]>([]);

  const togglePick = (idx: number): void => {
    setPicked((prev) => {
      if (prev.includes(idx)) return prev.filter((i) => i !== idx);
      if (prev.length >= MAX_MULLIGAN) return prev;
      return [...prev, idx];
    });
  };

  // Wire format stays cardIds[] (with duplicates allowed); the backend
  // handlers consume it with multi-set semantics.
  const submit = (): void => onSubmit(picked.map((i) => hand[i] ?? '').filter(Boolean));
  const keepAll = (): void => onSubmit([]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Mulligan — choose up to 3 cards to bury"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-cosmos-950/90 backdrop-blur-sm"
    >
      <div className="max-h-[90vh] w-[90vw] max-w-[1100px] overflow-hidden rounded-md border border-amber-700 bg-cosmos-900 shadow-2xl">
        <header className="flex items-center justify-between border-cosmos-800 border-b px-4 py-3">
          <div>
            <h2 className="font-bold text-amber-200 text-xl">Opening Hand — Mulligan</h2>
            <p className="text-cosmos-400 text-xs">
              Pick up to {MAX_MULLIGAN} cards to bury at the bottom of your deck. You'll draw the
              same number of replacements off the top.
            </p>
          </div>
          {onCancel ? (
            <button
              type="button"
              className="rounded bg-cosmos-800 px-2 py-1 text-cosmos-200 text-sm hover:bg-cosmos-700"
              onClick={onCancel}
            >
              ✕
            </button>
          ) : null}
        </header>

        <div className="grid grid-cols-7 gap-3 p-4">
          {hand.map((id, idx) => {
            const card = cards.get(id);
            const isPicked = picked.includes(idx);
            return (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: hand may hold duplicate card_ids; idx disambiguates the slot.
                key={`${id}-${idx}`}
                type="button"
                onClick={() => togglePick(idx)}
                className={`group relative aspect-[2/3] overflow-hidden rounded-md ring-2 transition ${
                  isPicked
                    ? 'ring-rose-500 grayscale'
                    : 'ring-cosmos-700 hover:ring-amber-300 hover:scale-105'
                }`}
                title={card?.name ?? id}
              >
                <img
                  src={imageUrl(id)}
                  alt={card?.name ?? id}
                  className="h-full w-full object-cover"
                  draggable={false}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                {isPicked ? (
                  <span className="absolute inset-0 flex items-center justify-center bg-rose-950/70 font-bold text-rose-100 text-xl">
                    BURY
                  </span>
                ) : null}
                <span className="absolute inset-x-0 bottom-0 truncate bg-cosmos-950/80 px-1 py-0.5 text-[11px] text-cosmos-100">
                  {card?.name ?? id}
                </span>
              </button>
            );
          })}
        </div>

        <footer className="flex items-center justify-between border-cosmos-800 border-t px-4 py-3">
          <span className="text-cosmos-400 text-xs">
            Picked {picked.length} of {MAX_MULLIGAN}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded bg-cosmos-700 px-3 py-1.5 text-cosmos-200 text-sm hover:bg-cosmos-600"
              onClick={keepAll}
            >
              Keep all
            </button>
            <button
              type="button"
              className="rounded bg-amber-600 px-3 py-1.5 font-semibold text-cosmos-950 text-sm hover:bg-amber-500 disabled:opacity-50"
              onClick={submit}
              disabled={picked.length === 0}
            >
              Bury {picked.length} & draw replacements
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
